import { useMemo, memo, useState } from 'react'
import { Box, Heading, HStack, Button, ButtonGroup, Tooltip, useColorModeValue, Spinner } from '@chakra-ui/react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts'
import { colors, components } from '../../theme'
import { STRINGS } from '../../resources'
import styles from './RiskFactorsChart.module.css'

interface RiskFactorsChartProps {
  riskFactorCounts: Record<string, number>  // Pre-computed by worker
  isFiltering?: boolean
}

const MAX_RISK_FACTORS = 10
const LABEL_MAX_LENGTH = 25

type ViewMode = 'risks' | 'all'

// Factors to exclude from the chart
// - Positive factors (not actually risks)
// - Severity classifications (redundant with severity column)
const EXCLUDED_FACTORS = [
  'has fix',           // Positive - indicates fix is available
  'critical severity', // Redundant with severity column
  'high severity',     // Redundant with severity column
  'medium severity',   // Redundant with severity column
  'low severity',      // Redundant with severity column
]

function shouldExcludeFactor(factor: string): boolean {
  const lower = factor.toLowerCase()
  return EXCLUDED_FACTORS.some(excluded => lower.includes(excluded))
}

export const RiskFactorsChart = memo(function RiskFactorsChart({ riskFactorCounts, isFiltering = false }: RiskFactorsChartProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('risks')

  const cardBg = useColorModeValue(colors.ui.card.light, colors.ui.card.dark)
  const textColor = useColorModeValue(colors.ui.text.secondary.light, colors.ui.text.secondary.dark)
  const overlayBg = useColorModeValue('whiteAlpha.700', 'blackAlpha.600')
  const activeBg = useColorModeValue('blue.500', 'blue.400')
  const inactiveBg = useColorModeValue('gray.100', 'gray.700')
  const activeColor = 'white'
  const inactiveColor = useColorModeValue('gray.600', 'gray.300')
  const barColor = colors.chart.primary

  // Use pre-computed counts from worker - no main thread loops!
  // Just filter/sort the keys (O(k) where k = unique factors, not O(n*m) vulns*factors)
  const riskFactorData = useMemo(() => {
    return Object.entries(riskFactorCounts)
      .filter(([name]) => viewMode === 'all' || !shouldExcludeFactor(name))
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_RISK_FACTORS)
      .map(([name, count]) => ({
        name: name.length > LABEL_MAX_LENGTH ? name.slice(0, LABEL_MAX_LENGTH - 1) + '…' : name,
        fullName: name,
        count,
      }))
  }, [riskFactorCounts, viewMode])

  if (riskFactorData.length === 0) {
    return null
  }

  return (
    <Box className={`${styles.container} card-glow`} bg={cardBg}>
      <HStack justify="space-between" align="center" mb={3}>
        <Heading fontSize={components.chart.title.fontSize} fontWeight={components.chart.title.fontWeight}>
          {STRINGS.CHARTS.TOP_RISK_FACTORS}
        </Heading>
        <ButtonGroup size="xs" isAttached variant="solid">
          <Tooltip label="Show only threat indicators (excludes positive factors like 'has fix' and severity labels)" hasArrow>
            <Button
              onClick={() => setViewMode('risks')}
              bg={viewMode === 'risks' ? activeBg : inactiveBg}
              color={viewMode === 'risks' ? activeColor : inactiveColor}
              _hover={{ bg: viewMode === 'risks' ? activeBg : 'gray.200' }}
              fontWeight={viewMode === 'risks' ? 'semibold' : 'normal'}
            >
              Risks
            </Button>
          </Tooltip>
          <Tooltip label="Show all risk factors including positive indicators and severity classifications" hasArrow>
            <Button
              onClick={() => setViewMode('all')}
              bg={viewMode === 'all' ? activeBg : inactiveBg}
              color={viewMode === 'all' ? activeColor : inactiveColor}
              _hover={{ bg: viewMode === 'all' ? activeBg : 'gray.200' }}
              fontWeight={viewMode === 'all' ? 'semibold' : 'normal'}
            >
              All
            </Button>
          </Tooltip>
        </ButtonGroup>
      </HStack>
      <Box className={styles.chartWrapper} position="relative">
        {isFiltering && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg={overlayBg}
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={10}
            borderRadius="md"
          >
            <Spinner size="md" color="blue.500" thickness="3px" />
          </Box>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={riskFactorData} layout="vertical" margin={{ left: 20 }}>
            <XAxis type="number" tickFormatter={(v) => v.toLocaleString()} tick={{ fontSize: 11, fill: textColor }} />
            <YAxis
              type="category"
              dataKey="name"
              width={180}
              tick={{ fontSize: 11, fill: textColor }}
            />
            <RechartsTooltip
              formatter={(value: number) => [value.toLocaleString(), STRINGS.CHARTS.VULNERABILITIES]}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
              contentStyle={{
                backgroundColor: cardBg,
                borderColor: textColor,
              }}
            />
            <Bar
              dataKey="count"
              radius={[0, 4, 4, 0]}
              fill={barColor}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  )
})
